/**
 * src/components/assets/LabelPrint.jsx
 * Route: /assets/label-print
 *
 * Print strategy: generate all QR codes as base64 PNGs,
 * then open a popup window with clean HTML + @page size → print.
 * This is the only reliable cross-browser approach for custom labels.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API  = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('iassets_token')}` });

/* ── Load qrcodejs from CDN ─────────────────────────────────── */
let qrScriptPromise = null;
function loadQRScript() {
  if (window.QRCode) return Promise.resolve(window.QRCode);
  if (qrScriptPromise)  return qrScriptPromise;
  qrScriptPromise = new Promise((resolve, reject) => {
    const s   = document.createElement('script');
    s.src     = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js';
    s.onload  = () => resolve(window.QRCode);
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return qrScriptPromise;
}

/* ── Generate one QR code as base64 PNG ─────────────────────── */
function generateQRBase64(text, size = 200) {
  return new Promise(resolve => {
    loadQRScript().then(QRCode => {
      const div = document.createElement('div');
      div.style.cssText = 'position:fixed;left:-9999px;top:-9999px;visibility:hidden';
      document.body.appendChild(div);
      new QRCode(div, { text, width: size, height: size,
        correctLevel: QRCode.CorrectLevel.M });
      setTimeout(() => {
        const canvas = div.querySelector('canvas');
        const img    = div.querySelector('img');
        const b64    = canvas ? canvas.toDataURL('image/png')
                      : img   ? img.src : '';
        document.body.removeChild(div);
        resolve(b64);
      }, 120);
    }).catch(() => resolve(''));
  });
}

/* ── Screen QR preview card ─────────────────────────────────── */
function QRPreviewCard({ asset }) {
  const [src, setSrc] = useState('');
  const url = `${window.location.origin}/a/${asset.asset_code}`;

  useEffect(() => {
    generateQRBase64(url, 130).then(setSrc);
  }, [url]);

  return (
    <div style={{ background:'#fff', border:'1.5px solid #e2e8f0', borderRadius:10,
      padding:'12px 10px', display:'flex', flexDirection:'column',
      alignItems:'center', gap:5, width:160, boxShadow:'0 1px 4px rgba(0,0,0,.05)' }}>
      {src
        ? <img src={src} alt={asset.asset_code} width={130} height={130}
            style={{ imageRendering:'pixelated',maxWidth:'85%' }}/>
        : <div style={{ width:130, height:130, background:'#f1f5f9',
            borderRadius:6, display:'flex', alignItems:'center',
            justifyContent:'center', fontSize:11, color:'#94a3b8' }}>
            Generating…
          </div>
      }
      <div style={{ fontSize:12, fontWeight:700, color:'#1a56db',
        fontFamily:'monospace', textAlign:'center', width:'100%' }}>
        {asset.asset_code}
      </div>
      <div style={{ fontSize:10, color:'#94a3b8', textAlign:'center' }}>
        Scan to view asset
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
export default function LabelPrint() {
  const [categories,  setCategories]  = useState([]);
  const [locations,   setLocations]   = useState([]);
  const [assets,      setAssets]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [fetching,    setFetching]    = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [printing,    setPrinting]    = useState(false);
  const [printMsg,    setPrintMsg]    = useState('');

  const [filterCat,  setFilterCat]  = useState('');
  const [filterLoc,  setFilterLoc]  = useState('');
  const [filterQ,    setFilterQ]    = useState('');
  const [selected,   setSelected]   = useState([]);

  const [widthMm,  setWidthMm]  = useState(35);
  const [heightMm, setHeightMm] = useState(35);

  /* Load masters */
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/masters/categories`, { headers: auth() }).catch(()=>({data:[]})),
      axios.get(`${API}/masters/locations`,  { headers: auth() }).catch(()=>({data:[]})),
    ]).then(([cr, lr]) => {
      setCategories(cr.data?.data || cr.data || []);
      setLocations(lr.data?.data  || lr.data  || []);
    }).finally(() => setLoading(false));
  }, []);

  /* Fetch assets on filter change */
  const fetchAssets = useCallback(async (cat, loc) => {
    if (!cat && !loc) return;
    setFetching(true); setHasSearched(true); setSelected([]);
    try {
      const p = new URLSearchParams({ limit: 5000 });
      if (cat) p.append('category_id', cat);
      if (loc) p.append('location_id',  loc);
      const r = await axios.get(`${API}/assets/?${p}`, { headers: auth() });
      setAssets(r.data?.data || r.data || []);
    } catch { setAssets([]); }
    finally { setFetching(false); }
  }, []);

  const filtered   = filterQ
    ? assets.filter(a => a.asset_code?.toLowerCase().includes(filterQ.toLowerCase())
        || a.name?.toLowerCase().includes(filterQ.toLowerCase()))
    : assets;

  const selectAll   = () => setSelected([...filtered]);
  const clearSelect = () => setSelected([]);
  const toggleOne   = a => setSelected(prev =>
    prev.some(x=>x.id===a.id) ? prev.filter(x=>x.id!==a.id) : [...prev, a]
  );

  const printList = selected.length > 0 ? selected : filtered;

  /* ── Print via popup window ────────────────────────────────── */
  const handlePrint = async () => {
    if (!printList.length) { alert('No assets to print.'); return; }
    setPrinting(true);

    // Generate all QR base64 images
    const qrSize = Math.round(Math.min(widthMm, heightMm) * 3.5);
    const entries = [];
    for (let i = 0; i < printList.length; i++) {
      const a = printList[i];
      setPrintMsg(`Generating QR ${i+1} of ${printList.length}…`);
      const url = `${window.location.origin}/a/${a.asset_code}`;
      const b64 = await generateQRBase64(url, qrSize);
      entries.push({ code: a.asset_code, src: b64 });
    }

    setPrintMsg('Opening print window…');

    // Build label HTML for each asset
    const labelCss = `
      * { margin:0; padding:0; box-sizing:border-box; }
      body { width:${widthMm}mm; }
      .lbl {
        width: ${widthMm}mm;
        height: ${heightMm}mm;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        page-break-after: always;
        break-after: page;
        overflow: hidden;
        padding: 1mm;
        gap: 0.5mm;
      }
      .lbl:last-child { page-break-after: avoid; break-after: avoid; }
      .lbl img {
        width: ${widthMm - 4}mm;
        height: ${widthMm - 4}mm;
        image-rendering: pixelated;
        display: block;        
      }
      .lbl .code {
        font-family: monospace;
        font-size: 7pt;
        font-weight: bold;
        color: #1a56db;
        text-align: center;
        width: 100%;
      }
      .lbl .scan {
        font-family: Arial, sans-serif;
        font-size: 5pt;
        color: #888;
        text-align: center;
        width: 100%;
      }
      @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
    `;

    const labelsHtml = entries.map(e => `
      <div class="lbl">
        ${e.src ? `<img src="${e.src}" alt="${e.code}"/>` : ''}
        <div class="code">${e.code}</div>
        <div class="scan">Scan to view asset</div>
      </div>
    `).join('');

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>QR Labels — ${printList.length} Labels</title>
<style>${labelCss}</style>
</head><body>${labelsHtml}</body></html>`;

    const popup = window.open('', '_blank',
      `width=${widthMm * 4 + 50},height=${heightMm * 4 + 100},toolbar=0,menubar=0`);
    if (!popup) {
      alert('Please allow popups for this site to print labels.');
      setPrinting(false); setPrintMsg(''); return;
    }
    popup.document.write(html);
    popup.document.close();
    popup.onload = () => {
      popup.focus();
      popup.print();
      popup.onafterprint = () => popup.close();
    };

    setPrinting(false);
    setPrintMsg('');
  };

  /* ── Styles ────────────────────────────────────────────────── */
  const IS = { border:'1.5px solid #e2e8f0', borderRadius:8, padding:'8px 11px',
    fontSize:13, outline:'none', background:'#fff', color:'#1a1f2e',
    width:'100%', boxSizing:'border-box' };
  const LS = { fontSize:11, fontWeight:700, color:'#475569', display:'block',
    marginBottom:4, textTransform:'uppercase', letterSpacing:'.04em' };

  return (
    <div style={{ fontFamily:'-apple-system,sans-serif',
      height:'calc(100vh - 80px)', display:'flex', flexDirection:'column' }}>

      {/* ── Header ─────────────────────────────────────────────── */}
      <div style={{ display:'flex', alignItems:'center', gap:12,
        marginBottom:14, flexWrap:'wrap' }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:800, color:'#1a1f2e', margin:0 }}>
            🏷️ Label Print — QR Codes
          </h1>
          <p style={{ fontSize:12, color:'#64748b', margin:'3px 0 0' }}>
            Select Category &amp; Location → Select All → Print
          </p>
        </div>
        <div style={{ flex:1 }}/>

        {/* Label size */}
        <div style={{ display:'flex', alignItems:'center', gap:8,
          background:'#f8fafc', border:'1px solid #e2e8f0',
          borderRadius:10, padding:'7px 12px', flexWrap:'wrap' }}>
          <span style={{ fontSize:12, fontWeight:600, color:'#64748b' }}>Label:</span>
          <input type="number" value={widthMm} min={20} max={200}
            onChange={e=>setWidthMm(Number(e.target.value))}
            style={{ width:50, border:'1.5px solid #e2e8f0', borderRadius:6,
              padding:'4px 6px', fontSize:12, textAlign:'center', outline:'none' }}/>
          <span style={{ fontSize:12, color:'#94a3b8' }}>×</span>
          <input type="number" value={heightMm} min={20} max={200}
            onChange={e=>setHeightMm(Number(e.target.value))}
            style={{ width:50, border:'1.5px solid #e2e8f0', borderRadius:6,
              padding:'4px 6px', fontSize:12, textAlign:'center', outline:'none' }}/>
          <span style={{ fontSize:12, color:'#94a3b8' }}>mm</span>
          {[['35×35',35,35],['50×50',50,50],['50×25',50,25],['100×50',100,50]].map(([l,w,h])=>(
            <button key={l} onClick={()=>{setWidthMm(w);setHeightMm(h);}}
              style={{ padding:'3px 8px', fontSize:10, fontWeight:600, cursor:'pointer',
                border:`1.5px solid ${widthMm===w&&heightMm===h?'#1a56db':'#e2e8f0'}`,
                borderRadius:6,
                background:widthMm===w&&heightMm===h?'#eff6ff':'#fff',
                color:widthMm===w&&heightMm===h?'#1a56db':'#64748b' }}>{l}</button>
          ))}
        </div>

        {/* Print button */}
        <button onClick={handlePrint}
          disabled={!hasSearched||fetching||printList.length===0||printing}
          style={{ padding:'10px 22px', border:'none', borderRadius:9,
            fontSize:14, fontWeight:700, whiteSpace:'nowrap',
            cursor:(hasSearched&&!fetching&&printList.length>0&&!printing)?'pointer':'not-allowed',
            background:(hasSearched&&!fetching&&printList.length>0&&!printing)?'#1a56db':'#e2e8f0',
            color:(hasSearched&&!fetching&&printList.length>0&&!printing)?'#fff':'#94a3b8',
            minWidth:160 }}>
          {printing ? printMsg || '⏳ Preparing…'
            : `🖨️ Print ${printList.length > 0 ? `${printList.length} Label${printList.length!==1?'s':''}` : '—'}`}
        </button>
      </div>

      {/* ── Body: Left + Right ─────────────────────────────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'280px 1fr',
        gap:14, flex:1, minHeight:0 }}>

        {/* LEFT — filters + list */}
        <div style={{ display:'flex', flexDirection:'column', gap:10,
          background:'#fff', border:'1px solid #e2e8f0',
          borderRadius:12, padding:'14px', overflow:'hidden' }}>

          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            <div>
              <label style={LS}>Category</label>
              <select value={filterCat}
                onChange={e=>{setFilterCat(e.target.value);fetchAssets(e.target.value,filterLoc);}}
                style={IS}>
                <option value="">— Select Category —</option>
                {categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={LS}>Location / Plant</label>
              <select value={filterLoc}
                onChange={e=>{setFilterLoc(e.target.value);fetchAssets(filterCat,e.target.value);}}
                style={IS}>
                <option value="">— Select Location —</option>
                {locations.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            {hasSearched && (
              <input value={filterQ} onChange={e=>setFilterQ(e.target.value)}
                placeholder="Search within results…" style={IS}/>
            )}
          </div>

          {/* Asset list header */}
          {hasSearched && !fetching && (
            <div style={{ display:'flex', alignItems:'center', gap:8,
              paddingTop:8, borderTop:'1px solid #f1f5f9', flexShrink:0 }}>
              <span style={{ fontSize:12, fontWeight:700, color:'#374151', flex:1 }}>
                {filtered.length} assets
                {selected.length > 0 && (
                  <span style={{ color:'#1a56db', marginLeft:4 }}>
                    ({selected.length} selected)
                  </span>
                )}
              </span>
              <button onClick={selectAll}
                style={{ fontSize:11, fontWeight:700, color:'#1a56db',
                  background:'none', border:'none', cursor:'pointer', padding:0 }}>
                Select All
              </button>
              <button onClick={clearSelect}
                style={{ fontSize:11, color:'#64748b', background:'none',
                  border:'none', cursor:'pointer', padding:0 }}>
                Clear
              </button>
            </div>
          )}

          {/* Asset rows */}
          <div style={{ flex:1, overflowY:'auto', minHeight:0 }}>
            {fetching ? (
              <div style={{ padding:30, textAlign:'center', color:'#94a3b8' }}>
                <div style={{ fontSize:24, marginBottom:6 }}>⏳</div>
                <div style={{ fontSize:12 }}>Loading assets…</div>
              </div>
            ) : !hasSearched ? (
              <div style={{ padding:30, textAlign:'center', color:'#94a3b8' }}>
                <div style={{ fontSize:32, marginBottom:8 }}>🔍</div>
                <div style={{ fontSize:12 }}>Select Category or Location<br/>to load assets</div>
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding:20, textAlign:'center', color:'#94a3b8', fontSize:12 }}>
                No assets found
              </div>
            ) : filtered.map(a => {
              const isSel = selected.some(x=>x.id===a.id);
              return (
                <div key={a.id} onClick={()=>toggleOne(a)}
                  style={{ display:'flex', alignItems:'center', gap:9,
                    padding:'7px 5px', borderBottom:'1px solid #f8fafc',
                    cursor:'pointer',
                    background:isSel?'#eff6ff':'transparent',
                    borderRadius:6, marginBottom:1 }}>
                  <div style={{ width:14, height:14, borderRadius:3, flexShrink:0,
                    border:`2px solid ${isSel?'#1a56db':'#d1d5db'}`,
                    background:isSel?'#1a56db':'#fff',
                    display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {isSel && <svg width="9" height="9" viewBox="0 0 9 9">
                      <path d="M1.5 4.5l2 2 4-4" stroke="#fff" strokeWidth="1.5"
                        fill="none" strokeLinecap="round"/>
                    </svg>}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'#1a56db',
                      fontFamily:'monospace' }}>{a.asset_code}</div>
                    <div style={{ fontSize:10, color:'#64748b',
                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {a.name}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {(filterCat||filterLoc) && (
            <button onClick={()=>{setFilterCat('');setFilterLoc('');setAssets([]);
              setHasSearched(false);setSelected([]);setFilterQ('');}}
              style={{ padding:'7px', border:'1px solid #fecaca', borderRadius:8,
                background:'#fff', fontSize:12, color:'#dc2626', cursor:'pointer',
                flexShrink:0 }}>
              ✕ Clear All Filters
            </button>
          )}
        </div>

        {/* RIGHT — QR preview */}
        <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0',
          borderRadius:12, overflow:'auto', padding:16 }}>
          {selected.length === 0 && !hasSearched ? (
            <div style={{ height:'100%', display:'flex', alignItems:'center',
              justifyContent:'center', flexDirection:'column', gap:10, color:'#94a3b8' }}>
              <div style={{ fontSize:52 }}>🏷️</div>
              <div style={{ fontSize:14, fontWeight:600, color:'#64748b' }}>
                QR label preview appears here
              </div>
              <div style={{ fontSize:12 }}>Select assets from the list</div>
            </div>
          ) : selected.length === 0 && hasSearched ? (
            <div style={{ height:'100%', display:'flex', alignItems:'center',
              justifyContent:'center', flexDirection:'column', gap:10, color:'#94a3b8' }}>
              <div style={{ fontSize:52 }}>☑️</div>
              <div style={{ fontSize:14, fontWeight:600, color:'#64748b' }}>
                Click "Select All" or tick assets to preview QR codes
              </div>
            </div>
          ) : (
            <>
              <div style={{ fontSize:12, fontWeight:600, color:'#64748b', marginBottom:12 }}>
                Showing {selected.length} QR label{selected.length>1?'s':''} preview
                <span style={{ fontSize:11, color:'#94a3b8', marginLeft:6 }}>
                  (print size: {widthMm}×{heightMm}mm)
                </span>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:12 }}>
                {selected.map(a => <QRPreviewCard key={a.id} asset={a}/>)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
